import React from 'react';
import { useFileUpload } from '../hooks/useFileUpload';
import { X, FileText } from 'lucide-react';

interface FileUploadProps {
  files: File[];
  onRemoveFile: (index: number) => void;
  onClearAll: () => void;
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onRemoveFile,
  onClearAll,
  className = ''
}) => {
  const { getFileIcon, formatFileSize } = useFileUpload();

  if (files.length === 0) {
    return null;
  }

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-3 shadow-sm ${className}`}>
      
      {files.length === 1 ? (
        // Один файл - улучшенный дизайн
        <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center space-x-3">
            {/* Иконка файла */}
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
              {getFileIcon(files[0])}
            </div>
            
            {/* Информация о файле */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate mb-1">
                {files[0].name}
              </p>
              <div className="flex items-center space-x-3 text-xs text-gray-500">
                <span className="bg-gray-100 px-2 py-1 rounded-full">
                  {formatFileSize(files[0].size)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Кнопка удаления */}
          <button
            onClick={() => onRemoveFile(0)}
            className="ml-3 p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200 group"
            title="Удалить файл"
          >
            <X className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        </div>
      ) : (
        // Несколько файлов - улучшенный дизайн
        <div className="space-y-3">
          {/* Заголовок с количеством файлов */}
          <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-semibold text-gray-700">
                Файлов: {files.length}
              </span>
            </div>
            <button
              onClick={onClearAll}
              className="text-xs text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-all duration-200 font-medium"
            >
              Удалить все
            </button>
          </div>
          
          {/* Список файлов */}
          <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all duration-200"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {/* Иконка файла */}
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 text-sm">
                    {getFileIcon(file)}
                  </div>

                  {/* Информация о файле */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate mb-1">
                      {file.name}
                    </p>
                    <div className="flex items-center space-x-2 text-xs">
                      <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Кнопка удаления */}
                <button
                  onClick={() => onRemoveFile(index)}
                  className="ml-3 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200 group flex-shrink-0"
                  title="Удалить файл"
                >
                  <X className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
